import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  BookOpen, 
  PenTool, 
  Calculator, 
  Globe, 
  Sparkles,
  Star,
  Trophy,
  Users,
  Settings,
  LogOut,
  Palette
} from "lucide-react";

// Import TeacherHubPage for teacher role
import TeacherHubPage from "./TeacherHubPage";

// Module definitions
const MODULES = [
  {
    id: "lecture",
    name: "Lecture",
    description: "Apprendre a lire avec les syllabes colorees",
    icon: BookOpen,
    color: "blue",
    bgColor: "bg-blue-500",
    lightBg: "bg-blue-50",
    borderColor: "border-blue-200",
    hoverBorder: "hover:border-blue-400",
    textColor: "text-blue-700",
    shadowColor: "shadow-[0_6px_0_rgb(29,78,216)]",
    available: true,
    path: "/lecture",
  },
  {
    id: "grammaire",
    name: "Grammaire",
    description: "Decouvrir les mots et les phrases",
    icon: PenTool,
    color: "purple",
    bgColor: "bg-purple-500",
    lightBg: "bg-purple-50",
    borderColor: "border-purple-200",
    hoverBorder: "hover:border-purple-400",
    textColor: "text-purple-700",
    shadowColor: "shadow-[0_6px_0_rgb(126,34,206)]",
    available: true,
    path: "/grammaire",
  },
  {
    id: "dictee",
    name: "Dictee",
    description: "Ecrire ce que tu entends",
    icon: PenTool,
    color: "green",
    bgColor: "bg-green-500",
    lightBg: "bg-green-50",
    borderColor: "border-green-200",
    hoverBorder: "hover:border-green-400",
    textColor: "text-green-700",
    shadowColor: "shadow-[0_6px_0_rgb(22,163,74)]",
    available: true,
    path: "/dictation",
  },
  {
    id: "mathematiques",
    name: "Mathematiques",
    description: "Compter et calculer",
    icon: Calculator,
    color: "orange",
    bgColor: "bg-orange-500",
    lightBg: "bg-orange-50",
    borderColor: "border-orange-200",
    hoverBorder: "hover:border-orange-400",
    textColor: "text-orange-700",
    shadowColor: "shadow-[0_6px_0_rgb(194,65,12)]",
    available: true,
    path: "/math",
  },
  {
    id: "anglais",
    name: "Anglais",
    description: "Decouvrir l'anglais",
    icon: Globe,
    color: "red",
    bgColor: "bg-red-500",
    lightBg: "bg-red-50",
    borderColor: "border-red-200",
    hoverBorder: "hover:border-red-400",
    textColor: "text-red-700",
    shadowColor: "shadow-[0_6px_0_rgb(185,28,28)]",
    available: true,
    path: "/english",
  },
];

function ModuleCard({ module, onClick, disabled }) {
  const Icon = module.icon;
  
  return (
    <button
      data-testid={`module-${module.id}`}
      onClick={() => !disabled && onClick(module)}
      disabled={disabled}
      className={`
        relative flex flex-col items-center gap-4 p-6 md:p-8
        ${module.lightBg} border-3 ${module.borderColor} ${!disabled && module.hoverBorder}
        rounded-3xl transition-all duration-300
        ${disabled 
          ? "opacity-50 cursor-not-allowed grayscale" 
          : "cursor-pointer hover:scale-105 active:scale-95 hover:shadow-xl"
        }
      `}
    >
      {!module.available && (
        <div className="absolute top-3 right-3 bg-slate-200 text-slate-500 text-xs font-bold px-2 py-1 rounded-full">
          Bientot
        </div>
      )}
      
      <div className={`
        w-20 h-20 md:w-24 md:h-24 ${module.bgColor} rounded-2xl 
        flex items-center justify-center ${module.shadowColor}
        transition-transform
      `}>
        <Icon size={40} className="text-white" />
      </div>
      
      <div className="text-center">
        <h3 className={`font-fredoka font-bold text-xl md:text-2xl ${module.textColor}`}>
          {module.name}
        </h3>
        <p className="text-slate-500 text-sm mt-1 font-nunito">
          {module.description}
        </p>
      </div>
    </button>
  );
}

export default function HomePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleModuleSelect = (module) => {
    if (!module.available) return;
    navigate(module.path);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isTeacher = user?.role === "teacher";
  const isParent = user?.role === "parent";

  // Redirect teachers to TeacherHubPage
  if (isTeacher) {
    return <TeacherHubPage />;
  }

  return (
    <div 
      data-testid="home-page"
      className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-blue-50"
    >
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/LEcoleDeNaviLogo.png" 
              alt="L'ecole de Navi" 
              className="h-10 md:h-12"
            />
          </div>
          
          <div className="flex items-center gap-2 md:gap-4">
            {/* Stars counter */}
            <button
              data-testid="rewards-btn"
              onClick={() => navigate("/rewards")}
              className="flex items-center gap-2 bg-yellow-100 hover:bg-yellow-200 px-3 py-2 rounded-full transition-colors"
            >
              <Star className="text-yellow-500" size={20} fill="currentColor" />
              <span className="font-fredoka font-bold text-yellow-700">12</span>
            </button>

            {/* User menu */}
            <div className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-full">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isParent ? "bg-green-500" : "bg-orange-500"
              }`}>
                <span className="text-white font-bold text-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="font-nunito font-bold text-slate-600 hidden md:inline">
                {user?.name}
              </span>
              <button
                data-testid="logout-btn"
                onClick={handleLogout}
                className="p-1 hover:bg-slate-200 rounded-full transition-colors"
              >
                <LogOut size={18} className="text-slate-500" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        {/* Welcome Section */}
        <div className="text-center mb-10 md:mb-14">
          <div className="flex justify-center mb-4">
            <img 
              src="/LEcoleDeNavi2.png" 
              alt="Navi" 
              className="h-24 md:h-32 mascot-float drop-shadow-lg"
            />
          </div>
          <h1 className="font-fredoka text-3xl md:text-5xl font-bold text-slate-800 mb-3">
            Bonjour {user?.name} !
          </h1>
          <p className="font-nunito text-lg md:text-xl text-slate-500">
            {isParent 
              ? "Suivez les progres de votre enfant"
              : "Qu'est-ce qu'on apprend aujourd'hui ?"
            }
          </p>
        </div>

        {/* User info banner for students/parents */}
        {user?.niveau && (
          <div className="mb-8 flex justify-center">
            <div className="inline-flex items-center gap-3 bg-white/80 backdrop-blur px-5 py-3 rounded-full border border-slate-200 shadow-sm">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                user?.niveau === "gs" ? "bg-pink-400" :
                user?.niveau === "cp" ? "bg-blue-400" :
                user?.niveau === "ce1" ? "bg-green-400" :
                user?.niveau === "ce2" ? "bg-yellow-400" :
                user?.niveau === "cm1" ? "bg-orange-400" :
                "bg-red-400"
              }`}>
                <span className="text-white font-bold text-xs">{user?.niveau?.toUpperCase()}</span>
              </div>
              <span className="font-nunito font-bold text-slate-600">
                Niveau {user?.niveau?.toUpperCase()}
              </span>
              {isParent && user?.student_name && (
                <span className="text-slate-400">
                  • Parent de {user.student_name}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Modules Grid */}
        <div className="mb-10">
          <h2 className="font-fredoka text-xl md:text-2xl font-bold text-slate-700 text-center mb-6">
            Choisis ton activite
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {MODULES.map((module) => (
              <ModuleCard
                key={module.id}
                module={module}
                onClick={handleModuleSelect}
                disabled={!module.available}
              />
            ))}
          </div>
        </div>

        {/* Coming Soon Modules Notice */}
        <div className="mt-10 text-center">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur px-4 py-2 rounded-full border border-slate-200">
            <Sparkles className="text-yellow-500" size={18} />
            <span className="font-nunito text-sm text-slate-500">
              D'autres modules arrivent bientot !
            </span>
          </div>
        </div>
      </main>

      {/* Footer Mascot */}
      <div className="fixed bottom-4 right-4 opacity-30 pointer-events-none hidden md:block">
        <img 
          src="/LEcoleDeNavi4.png" 
          alt="" 
          className="h-32"
        />
      </div>
    </div>
  );
}
